/**
 * Travelling Fee Dedupe — theme-side workaround (v3: silent apply).
 *
 * Problem: ACO charges the "Travelling - AHC Service Areas" fee once per
 * line-item instead of once per order. Two products each opting into
 * Travelling leaves the customer charged J$5,000 twice.
 *
 * Why theme JS instead of a Shopify Function:
 *   - Discount Function API can't read ACO's `_acoFee` / "Product Group"
 *     line-item properties on this shop (attribute(key:) returns null).
 *   - Cart Transform functions are Shopify Plus only. AHC is on standard.
 *
 * Why silent background apply instead of click interception:
 *   - Pickeasy owns the cart's checkout flow and submits to a deprecated
 *     /cart?step=contact_information endpoint. It hijacks the CHECK OUT
 *     click before any other listener can run, so we can't intercept.
 *   - An /discount/CODE?redirect=... navigation on cart load clobbered
 *     Pickeasy's cart state via Clear-Site-Data side effects.
 *
 * This version instead does a credentialed fetch to /discount/CODE with
 * redirect:"manual". Shopify returns a 302 with a Set-Cookie that attaches
 * the discount to the session. We never follow the 302 — no navigation, no
 * Clear-Site-Data, no interference with Pickeasy. By the time the customer
 * reaches a working checkout (through any path), the discount is pre-
 * attached to the session.
 *
 * Cleanup: if the cart falls below the 2-Travelling-fees threshold, we
 * post to /discount/CODE/clear (alternatively rely on Shopify's own
 * expiry when cart changes significantly).
 */
(function () {
  "use strict";

  var DEDUPE_CODE = "TRAVEL-ADJUSTMENT";
  var TRAVELLING_PREFIX = "Travelling- AHC Service Areas";
  var APPLIED_COOKIE = "ahc_td_applied";

  function getCookie(name) {
    var m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return m ? decodeURIComponent(m[2]) : null;
  }

  function setCookie(name, value, maxAgeSec) {
    document.cookie =
      name +
      "=" +
      encodeURIComponent(value) +
      "; Max-Age=" +
      maxAgeSec +
      "; Path=/; SameSite=Lax";
  }

  function isTravellingLine(item) {
    var props = item.properties || {};
    var fee = props._acoFee;
    var group = props["Product Group"] || "";
    return (
      fee != null &&
      !isNaN(parseFloat(fee)) &&
      parseFloat(fee) > 0 &&
      String(group).indexOf(TRAVELLING_PREFIX) === 0
    );
  }

  function cartFingerprint(cart) {
    return (cart.items || [])
      .map(function (i) {
        return (
          i.key +
          ":" +
          i.quantity +
          ":" +
          (isTravellingLine(i) ? "T" : "N")
        );
      })
      .join("|");
  }

  function fetchCart() {
    return fetch("/cart.js", {
      cache: "no-store",
      credentials: "include",
    }).then(function (r) {
      return r.json();
    });
  }

  // Hit /discount/CODE without following the redirect. Shopify still
  // attaches the discount to the customer's session via Set-Cookie.
  function silentApply() {
    return fetch(
      "/discount/" +
        encodeURIComponent(DEDUPE_CODE) +
        "?_t=" +
        Date.now(),
      {
        method: "GET",
        credentials: "include",
        redirect: "manual",
        cache: "no-store",
      },
    ).catch(function () {
      /* best-effort */
    });
  }

  function evaluate() {
    fetchCart()
      .then(function (cart) {
        if (!cart || !cart.items) return;
        var count = cart.items.filter(isTravellingLine).length;
        var sig = cartFingerprint(cart);
        var last = getCookie(APPLIED_COOKIE);

        if (count >= 2 && last !== sig) {
          // Mark first so concurrent cart mutations don't retrigger.
          setCookie(APPLIED_COOKIE, sig, 60 * 60 * 6);
          silentApply();
        } else if (count < 2 && last) {
          // Cart no longer qualifies — clear our flag. (Shopify will
          // auto-revoke the discount if cart min-qty drops below 2.)
          setCookie(APPLIED_COOKIE, "", 0);
        }
      })
      .catch(function () {});
  }

  var pending = null;
  function queueEvaluate() {
    if (pending) clearTimeout(pending);
    pending = setTimeout(evaluate, 600);
  }

  // Initial pass once the page is ready.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", evaluate);
  } else {
    evaluate();
  }

  // Re-evaluate after ajax cart mutations from the theme, ACO, or others.
  var origFetch = window.fetch;
  if (typeof origFetch === "function") {
    window.fetch = function () {
      var url = "";
      try {
        url =
          typeof arguments[0] === "string"
            ? arguments[0]
            : (arguments[0] && arguments[0].url) || "";
      } catch (_) {}
      var res = origFetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)\.js/.test(url)) {
        Promise.resolve(res).then(queueEvaluate, queueEvaluate);
      }
      return res;
    };
  }

  // Ditto for XMLHttpRequest-based ajax (Pickeasy, ACO's frontend js).
  var origXhrOpen = XMLHttpRequest.prototype.open;
  var origXhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__ahcUrl = url || "";
    return origXhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    var url = this.__ahcUrl || "";
    if (/\/cart\/(add|change|update|clear)\.js/.test(url)) {
      this.addEventListener("loadend", queueEvaluate);
    }
    return origXhrSend.apply(this, arguments);
  };
})();
